import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SaSiblingCallouts } from "./patio-covers.in.san-antonio";
import hero from "@/assets/original/stock-porch.jpg";
import heroPicture from "@/assets/original/stock-porch.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/insulated-patio-covers/in/san-antonio";
const TITLE = "Insulated Patio Covers in San Antonio, TX | Texas Made Shade";
const DESC = "Snap-N-Lock style insulated patio covers in San Antonio, TX — true R-value roof panels that block heat, block rain and stay cool to the touch. Welded aluminum frames built in Seguin.";

export const Route = createFileRoute("/insulated-patio-covers/in/san-antonio")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Insulated Snap-N-Lock patio cover panel installed in San Antonio",
      socialTitle: "Insulated Patio Covers in San Antonio, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Insulated Patio Covers in San Antonio, TX",
        breadcrumbName: "Insulated Patio Covers in San Antonio",
        description: DESC,
        faqs: [
          { q: "How much cooler is an insulated patio cover?", a: "On a 100°F San Antonio afternoon under-cover air temps drop 25–35°F vs. open patio and the underside of the panel stays cool enough to touch — meaningfully cooler than a metal R-panel or fabric cover." },
          { q: "What R-value are your insulated panels?", a: "Standard 3-inch foam-core panels are R-13. 6-inch panels are R-26 and are common over outdoor kitchens where heat from grills and smokers stacks under the cover." },
          { q: "Do insulated covers stop rain?", a: "Yes — fully waterproof. Panels lock together with a tongue-and-groove seam and the perimeter gets a gutter integrated into the fascia. Many San Antonio customers add downspouts that drain to existing landscape drainage." },
          { q: "Can lights and ceiling fans mount to the underside?", a: "Yes — the underside is smooth aluminum, finished white or wood-grain, and accepts recessed cans, junction boxes and ceiling fan mounts. We coordinate with your electrician." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Insulated Patio Covers in San Antonio, TX",
          serviceType: "Insulated Patio Cover Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "San Antonio", containedInPlace: { "@type": "AdministrativeArea", name: "Bexar County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Insulated Patio Covers in San Antonio"
      breadcrumbs={[
        { label: "Patio Covers", to: "/patio-covers/in/san-antonio" },
        { label: "Insulated Patio Covers in San Antonio, TX" },
      ]}
      eyebrow="★ INSULATED PATIO COVERS · SAN ANTONIO, TX ★"
      title="Insulated Patio Covers in San Antonio, TX"
      intro="Foam-core insulated roof panels (Snap-N-Lock style) on a welded aluminum frame — the only patio cover that genuinely beats San Antonio summer heat. R-13 or R-26 panels, waterproof, with a smooth white or wood-grain underside ready for fans and lights."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Insulated Roof Panels · Serving San Antonio, TX",
        headline: "Insulated Patio Cover Quote — Free On-Site Estimate",
        subhead: "Send photos and rough dimensions of the covered area. We'll quote panel thickness, finish and gutter setup in one written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Patio / Pergola Cover")}&notes=${encodeURIComponent("Insulated patio cover — San Antonio, TX.")}`,
        primaryLabel: "Get My Insulated Cover Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "3-inch (R-13) or 6-inch (R-26) foam-core panels",
        "Fully waterproof — integrated gutter & downspouts",
        "Cool-to-the-touch underside in San Antonio summer",
        "White or wood-grain finished ceiling — accepts fans & lights",
        "Welded aluminum structural frame",
        "Engineered for 60+ mph wind & heavy hail",
      ]}
      body={[
        {
          heading: "The Only Cover That Actually Stops Heat",
          text: "A metal R-panel roof radiates Texas sun straight down — you can feel the heat dump off the underside in late afternoon. A fabric cover is great for UV but doesn't slow conduction. Insulated foam-core panels are the only structural cover that drops under-cover temps 25–35°F on a 100°F San Antonio day.",
        },
        {
          heading: "Built for Outdoor Kitchens & Living Rooms",
          text: "If you have a built-in grill, smoker, pizza oven or full outdoor kitchen, an insulated cover keeps the cook area livable and protects ceiling fans and TVs from rain. We spec 6-inch R-26 panels over kitchens where grill heat would otherwise stack under the cover.",
          cta: { label: "Pool & spa shade", href: "/pool-spa" },
        },
        {
          heading: "Waterproof — Real Rain Protection",
          text: "Panels lock with a tongue-and-groove seam and we integrate a gutter and downspouts on the perimeter — usually draining to existing landscape drainage. Use the patio in heavy San Antonio thunderstorms without water blowing under the cover.",
          cta: { label: "Aluminum patio covers", href: "/aluminum-patio-covers/in/san-antonio" },
        },
      ]}
      applications={[
        "Outdoor Kitchen Covers",
        "Living-Room-Style Patios",
        "Pool & Hot-Tub Cabanas",
        "Detached Pavilions",
        "Commercial Patio Bars",
        "Carports with Living Space Above",
      ]}
      faqs={[
        { q: "How much cooler?", a: "25–35°F under-cover vs. open patio on a 100°F San Antonio afternoon. The underside stays cool to the touch." },
        { q: "What R-value?", a: "R-13 (3-inch) standard. R-26 (6-inch) for outdoor kitchens or anywhere grill/smoker heat would stack." },
        { q: "Waterproof?", a: "Yes — tongue-and-groove panel seam plus integrated perimeter gutter and downspouts." },
        { q: "Can I mount fans and lights?", a: "Yes — smooth white or wood-grain underside accepts recessed cans, J-boxes and ceiling fan mounts." },
      ]}
      cityCallouts={[
        { to: "/san-antonio", city: "San Antonio", anchorText: "All Texas Made Shade services in San Antonio", blurb: "See every cover, awning and sail-shade we offer to San Antonio." },
        { to: "/aluminum-patio-covers/in/san-antonio", city: "San Antonio", anchorText: "Aluminum patio covers in San Antonio", blurb: "The welded aluminum frame that carries every insulated cover." },
      ]}
      postCta={<SaSiblingCallouts current={PATH} />}
    />
  );
}
